import { ChevronDown, ChevronRight, Search, AlertCircle } from 'lucide-react'
import type { ReviewTreeNode } from '../types'

type ReviewTreeItemProps = {
  node: ReviewTreeNode
  onToggle: (nodeId: number | string) => void
  searchQuery?: string
  highlightMissing?: boolean
}

// Parse measureReference string (e.g., "5,9,11") into display codes (e.g., ["B5", "G1", "G3"])
function parseMeasureReference(ref: string | null): string[] {
  if (!ref) return []

  return ref.split(',').map(id => {
    const num = parseInt(id.trim(), 10)
    if (isNaN(num)) return id.trim()

    // B measures are 1-8, G measures are mapped from higher numbers
    // This is a simplified mapping - adjust based on actual measure IDs
    if (num >= 1 && num <= 8) {
      return `B${num}`
    } else if (num >= 9 && num <= 14) {
      return `G${num - 8}`
    } else {
      return `M${num}`
    }
  })
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text

  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const index = lowerText.indexOf(lowerQuery)

  if (index === -1) return text

  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-yellow-200 rounded px-0.5">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  )
}

export function ReviewTreeItem({ node, onToggle, searchQuery, highlightMissing = false }: ReviewTreeItemProps) {
  const hasMeasures = node.measureReference || (node.measureIds && node.measureIds.length > 0)
  const measureCodes = parseMeasureReference(node.measureReference)
  const isInfoSearch = node.isInfoSearch === 1

  const displayText = node.text || node.actionSentence || node.planSentence || 'Unnamed Action'
  const showMissingWarning = highlightMissing && !hasMeasures && !node.isLeafNode === false

  return (
    <div className="py-1">
      <div
        className={`flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 ${
          showMissingWarning ? 'bg-red-50 border-l-2 border-red-400' : ''
        }`}
      >
        {/* Expand/collapse toggle */}
        <button
          onClick={() => onToggle(node.id)}
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center ${
            node.isLeafNode ? 'invisible' : 'text-gray-400 hover:text-gray-600'
          }`}
          disabled={node.isLeafNode}
        >
          {node.isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Info search indicator */}
        {isInfoSearch && (
          <Search className="flex-shrink-0 w-4 h-4 text-blue-500 mt-0.5" />
        )}

        {/* Action text */}
        <div className="flex-1 min-w-0">
          <span className="text-sm text-gray-700">
            {highlightText(displayText, searchQuery || '')}
          </span>
        </div>

        {/* Measure badges */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {measureCodes.length > 0 ? (
            measureCodes.map((code, idx) => (
              <span
                key={idx}
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  code.startsWith('B')
                    ? 'bg-blue-100 text-blue-700'
                    : code.startsWith('G')
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {code}
              </span>
            ))
          ) : showMissingWarning ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
              <AlertCircle className="w-3 h-3" />
              No measures
            </span>
          ) : null}
        </div>
      </div>

      {/* Children (if expanded) */}
      {node.isExpanded && node.children.length > 0 && (
        <div className="ml-6 border-l border-gray-200 pl-2">
          {node.children.map(child => (
            <ReviewTreeItem
              key={child.id}
              node={child}
              onToggle={onToggle}
              searchQuery={searchQuery}
              highlightMissing={highlightMissing}
            />
          ))}
        </div>
      )}
    </div>
  )
}
